import React, { useMemo, useState } from "react";
import { FaAngleDoubleDown, FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleDoubleUp, FaAngleDown, FaAngleLeft, FaAngleRight, FaAngleUp } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import type { U2000TableColumn } from "../../basic/U2000BasicTableList";
import type { U2000SearchStateHandlers } from "../../../../reducer/formReducer/U2000SearchStateHandlers";
import { u2000SortGridColumns } from "../../../../tools/u2000SearchCriteriaFieldConditions";
import { u2000TransferAllToSelected } from "../../../../tools/u2000TransferAllToSelected";

// Column type (adapt if you have a more specific one)

interface U2000BasicSearchFieldProps {
  searchStateHandlers: U2000SearchStateHandlers;
}

export type U2000FieldItem = {
  field: U2000TableColumn;
};

const U2000BasicSearchField: React.FC<U2000BasicSearchFieldProps> = ({ searchStateHandlers }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const fieldItems: U2000FieldItem[] = searchStateHandlers.getFields() || [];
  const allColumns = searchStateHandlers.getColumns();
  const sortedGridColumns = useMemo(
    () => u2000SortGridColumns(allColumns, locale),
    [allColumns, locale],
  );
  const availableGridColumns = useMemo(
    () => sortedGridColumns.filter((col) => fieldItems.findIndex((item) => item.field.name === col.name) === -1),
    [sortedGridColumns, fieldItems],
  );
  const [fieldName, setFieldName] = useState<string | null>(null);
  const [selectedFieldName, setSelectedFieldName] = useState<string | null>(null);

  // Ajoute le champ sélectionné à la liste des champs triés
  function onAddField() {
    if (fieldName) {
      const field = searchStateHandlers.getColumns().find((c) => c.name === fieldName);
      if (field) {
        const fieldToAdd = { field: { ...field } };
        // setSearchState((prev: any) => ({
        //   ...prev,
        //   section: {
        //     ...prev.section,
        //     fields: [...prev.section.fields, fieldToAdd],
        //   },
        // }));
        searchStateHandlers.addOrUpdateFields(searchStateHandlers.getFields().concat([fieldToAdd]));
        setFieldName(null);
      }
    }
  }

  // BL-0418 — Ajoute tous les champs disponibles à la sélection (concat, pas swap)
  function onAddAllFields() {
    const newFields = availableGridColumns.map((field: U2000TableColumn) => ({ field: { ...field } }));
    searchStateHandlers.addOrUpdateFields(
      u2000TransferAllToSelected(searchStateHandlers.getFields(), newFields),
    );

    setFieldName(null);
  }

  // Retire le champ sélectionné de la liste des champs triés
  function onRemoveField() {
    if (selectedFieldName) {
      const field = fieldItems.find((c: any) => c.field.name === selectedFieldName);
      if (field) {
        // setSearchState((prev: any) => ({
        //   ...prev,
        //   section: {
        //     ...prev.section,
        //     fields: prev.section.fields.filter((item: any) => item.field.name !== selectedFieldName),
        //   },
        // }));
        //
        searchStateHandlers.addOrUpdateFields(searchStateHandlers.getFields().filter((item: U2000FieldItem) => item.field.name !== selectedFieldName));
        setSelectedFieldName(null);
      }
    }
  }

  // Vide la liste des champs triés
  function onRemoveAllFields() {
    // setSearchState((prev: any) => ({
    //   ...prev,
    //   section: {
    //     ...prev.section,
    //     fields: [],
    //   },
    // }));
    //
    searchStateHandlers.addOrUpdateFields([]);
    setSelectedFieldName(null);
  }

  /**
   * Déplace le champ sélectionné dans la liste des champs triés
   * @param direction "up" | "down" | "top" | "bottom"
   */
  function moveItem(direction: "up" | "down" | "top" | "bottom") {
    if (!selectedFieldName) return;
    searchStateHandlers.moveFieldItem(selectedFieldName, direction);
  }

  const leftItems = availableGridColumns.length;
  // Nombre de champs déjà sélectionnés pour le tri
  const rightItems = fieldItems.length;

  return (
    <>
      <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-4">
        <div>
          {/* Available fields */}
          <div className="rounded-lg border border-gray-300 bg-white shadow-xs overflow-hidden">
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
              <p className="text-sm font-medium text-gray-900">{t("lblSearchFieldAvailable")}</p>
            </div>
            <div className="h-[140px] overflow-y-auto">
              {availableGridColumns.map((column) => (
                  <div
                    key={column.name}
                    className={`cursor-pointer px-4 py-2 text-sm transition-colors duration-150 ease-in-out
                              hover:bg-blue-50 focus:bg-blue-50
                              ${fieldName === column.name ? "bg-blue-100 text-blue-900 border-r-2 border-blue-500" : "text-gray-700 hover:text-gray-900"}`}
                    onClick={() => {
                      if (fieldName === column.name) {
                        setFieldName(null);
                      } else {
                        setFieldName(column.name);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (fieldName === column.name) {
                          setFieldName(null);
                        } else {
                          setFieldName(column.name);
                        }
                      }
                    }}
                  >
                    {column.label}
                  </div>
                ))}
              {availableGridColumns.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-gray-500 italic">Aucun champ disponible</div>
              )}
            </div>
          </div>
        </div>
        {/* Action for available fields */}
        <div className="flex flex-col justify-center gap-2">
          {/* FIRST */}
          <button
            aria-label="Ajouter tous les champs"
            className={`flex items-center justify-center w-10 h-10 rounded-lg text-white transition-all duration-200 ease-in-out
                      focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 active:scale-95
                      ${leftItems === 0 ? "bg-gray-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 shadow-xs hover:shadow-md"}`}
            onClick={() => onAddAllFields()}
            disabled={leftItems === 0}
            tabIndex={leftItems === 0 ? -1 : 0}
          >
            <FaAngleDoubleRight className="text-sm" />
          </button>
          {/* LEFT  */}
          <button
            aria-label="Ajouter le champ sélectionné"
            className={`flex items-center justify-center w-10 h-10 rounded-lg text-white transition-all duration-200 ease-in-out
                      focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 active:scale-95
                      ${leftItems === 0 || fieldName === null ? "bg-gray-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 shadow-xs hover:shadow-md"}`}
            onClick={() => {
              if (leftItems !== 0 && fieldName !== null) onAddField();
            }}
            disabled={leftItems === 0 || fieldName === null}
            tabIndex={leftItems === 0 || fieldName === null ? -1 : 0}
          >
            <FaAngleRight className="text-sm" />
          </button>

          {/* RIGHT */}
          <button
            aria-label="Retirer le champ sélectionné"
            className={`flex items-center justify-center w-10 h-10 rounded-lg text-white transition-all duration-200 ease-in-out
                      focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 active:scale-95
                      ${rightItems === 0 || selectedFieldName === null ? "bg-gray-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 shadow-xs hover:shadow-md"}`}
            onClick={() => {
              if (rightItems !== 0 && selectedFieldName !== null) onRemoveField();
            }}
            disabled={rightItems === 0 || selectedFieldName === null}
            tabIndex={rightItems === 0 || selectedFieldName === null ? -1 : 0}
          >
            <FaAngleLeft className="text-sm" />
          </button>
          {/* LAST */}
          <button
            aria-label="Retirer tous les champs"
            className={`flex items-center justify-center w-10 h-10 rounded-lg text-white transition-all duration-200 ease-in-out
                      focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 active:scale-95
                      ${rightItems === 0 ? "bg-gray-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 shadow-xs hover:shadow-md"}`}
            onClick={() => onRemoveAllFields()}
            disabled={rightItems === 0}
            tabIndex={rightItems === 0 ? -1 : 0}
          >
            <FaAngleDoubleLeft className="text-sm" />
          </button>
        </div>
        {/* Selected fields */}
        <div>
          <div className="rounded-lg border border-gray-300 bg-white shadow-xs overflow-hidden">
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
              <p className="text-sm font-medium text-gray-900">{t("lblSearchFieldSelected")}</p>
            </div>
            <div className="h-[140px] overflow-y-auto">
              {fieldItems.map((column) => (
                <div
                  key={column.field.name}
                  className={`cursor-pointer px-4 py-2 text-sm transition-colors duration-150 ease-in-out
                            hover:bg-blue-50 focus:bg-blue-50
                            ${selectedFieldName === column.field.name ? "bg-blue-100 text-blue-900 border-r-2 border-blue-500" : "text-gray-700 hover:text-gray-900"}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (selectedFieldName === column.field.name) {
                      setSelectedFieldName(null);
                    } else {
                      setSelectedFieldName(column.field.name);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      if (selectedFieldName === column.field.name) {
                        setSelectedFieldName(null);
                      } else {
                        setSelectedFieldName(column.field.name);
                      }
                    }
                  }}
                >
                  {column.field.label}
                </div>
              ))}
              {fieldItems.length === 0 && <div className="px-4 py-6 text-center text-sm text-gray-500 italic">Aucun champ sélectionné</div>}
            </div>
          </div>
        </div>
        {/* Action for selected fields */}
        <div className="flex flex-col justify-center gap-2">
          {/* TOP */}
          <button
            aria-label="Déplacer en haut le champ sélectionné"
            className={`flex items-center justify-center w-10 h-10 rounded-lg text-white transition-all duration-200 ease-in-out
                      focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 active:scale-95
                      ${
                        selectedFieldName === null || fieldItems.findIndex((item: any) => item.field.name === selectedFieldName) === 0
                          ? "bg-gray-300 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700 shadow-xs hover:shadow-md"
                      }`}
            onClick={() => {
              const idx = fieldItems.findIndex((item: any) => item.field.name === selectedFieldName);
              if (selectedFieldName !== null && idx > 0) moveItem("top");
            }}
            disabled={selectedFieldName === null || fieldItems.findIndex((item: any) => item.field.name === selectedFieldName) === 0}
            tabIndex={selectedFieldName === null || fieldItems.findIndex((item: any) => item.field.name === selectedFieldName) === 0 ? -1 : 0}
          >
            <FaAngleDoubleUp className="text-sm" />
          </button>
          {/* UP */}
          <button
            aria-label="Monter le champ sélectionné"
            className={`flex items-center justify-center w-10 h-10 rounded-lg text-white transition-all duration-200 ease-in-out
                      focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 active:scale-95
                      ${
                        selectedFieldName === null || fieldItems.findIndex((item) => item.field.name === selectedFieldName) === 0
                          ? "bg-gray-300 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700 shadow-xs hover:shadow-md"
                      }`}
            onClick={() => {
              const idx = fieldItems.findIndex((item) => item.field.name === selectedFieldName);
              if (selectedFieldName !== null && idx > 0) moveItem("up");
            }}
            disabled={selectedFieldName === null || fieldItems.findIndex((item) => item.field.name === selectedFieldName) === 0}
            tabIndex={selectedFieldName === null || fieldItems.findIndex((item) => item.field.name === selectedFieldName) === 0 ? -1 : 0}
          >
            <FaAngleUp className="text-sm" />
          </button>
          {/* DOWN */}
          <button
            aria-label="Descendre le champ sélectionné"
            className={`flex items-center justify-center w-10 h-10 rounded-lg text-white transition-all duration-200 ease-in-out
                      focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 active:scale-95
                      ${
                        selectedFieldName === null ||
                        fieldItems.findIndex((item) => item.field.name === selectedFieldName) === -1 ||
                        fieldItems.findIndex((item) => item.field.name === selectedFieldName) === fieldItems.length - 1
                          ? "bg-gray-300 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700 shadow-xs hover:shadow-md"
                      }`}
            onClick={() => {
              const idx = fieldItems.findIndex((item) => item.field.name === selectedFieldName);
              if (selectedFieldName !== null && idx !== -1 && idx < fieldItems.length - 1) moveItem("down");
            }}
            disabled={
              selectedFieldName === null ||
              fieldItems.findIndex((item) => item.field.name === selectedFieldName) === -1 ||
              fieldItems.findIndex((item) => item.field.name === selectedFieldName) === fieldItems.length - 1
            }
            tabIndex={
              selectedFieldName === null ||
              fieldItems.findIndex((item) => item.field.name === selectedFieldName) === -1 ||
              fieldItems.findIndex((item) => item.field.name === selectedFieldName) === fieldItems.length - 1
                ? -1
                : 0
            }
          >
            <FaAngleDown className="text-sm" />
          </button>
          {/* BOTTOM */}
          <button
            aria-label="Déplacer en bas le champ sélectionné"
            className={`flex items-center justify-center w-10 h-10 rounded-lg text-white transition-all duration-200 ease-in-out
                      focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 active:scale-95
                      ${
                        selectedFieldName === null ||
                        fieldItems.findIndex((item) => item.field.name === selectedFieldName) === -1 ||
                        fieldItems.findIndex((item) => item.field.name === selectedFieldName) === fieldItems.length - 1
                          ? "bg-gray-300 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700 shadow-xs hover:shadow-md"
                      }`}
            onClick={() => {
              const idx = fieldItems.findIndex((item) => item.field.name === selectedFieldName);
              if (selectedFieldName !== null && idx !== -1 && idx < fieldItems.length - 1) moveItem("bottom");
            }}
            disabled={
              selectedFieldName === null ||
              fieldItems.findIndex((item) => item.field.name === selectedFieldName) === -1 ||
              fieldItems.findIndex((item) => item.field.name === selectedFieldName) === fieldItems.length - 1
            }
            tabIndex={
              selectedFieldName === null ||
              fieldItems.findIndex((item) => item.field.name === selectedFieldName) === -1 ||
              fieldItems.findIndex((item) => item.field.name === selectedFieldName) === fieldItems.length - 1
                ? -1
                : 0
            }
          >
            <FaAngleDoubleDown className="text-sm" />
          </button>
        </div>
      </div>
    </>
  );
};

export default U2000BasicSearchField;
